<script setup>
// A network's own page on the website, drawn from the network page
// template's blocks (network_header, network_map, …). Laid out from
// the design's Network screen, in the same vocabulary as a trail's
// page (TrailDetail.vue) so the two read as one site: the same map
// drawing, hairline figures, hazard boxes and rows.
//
// Every number arrives from the server (App\Support\Website\SiteNetwork)
// and the weather from the site's own forecast for this network
// (SiteData forecast_networks), so nothing here measures anything of
// its own.
import { computed, inject, onMounted, ref, unref } from 'vue';
import { contourDrawing } from '@/lib/trailContours.js';
import HazardIcon from '@/Components/HazardIcon.vue';
import DifficultyIcon from '@/Components/DifficultyIcon.vue';
import { publicHazardTone } from '@/lib/publicHazard.js';
import { declutter } from '@/lib/mapDeclutter.js';
import { DIFFICULTY_KEYS, difficultyLabel } from '@/lib/trailLabels.js';
import { iconSetForUse } from '@/lib/trailIconSet.js';
import { parseCalendarDate } from '@/lib/calendarDate.js';
import { forecastFor, forecastSlots, forecastTemp, forecastWords } from './forecastSlots.js';
import { lengthText } from './siteLength.js';

const props = defineProps({
    config: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
    // 'header', 'map', 'details', 'about', 'trails', 'weather',
    // 'trailheads', 'hazards' or 'others'.
    part: { type: String, required: true },
});
const heading = (fallback, key = 'title') => String(props.config?.[key] || '').trim() || fallback;

const network = inject('tiqCurrentNetwork', null);
// A plain object on the site; a ref in the builder.
const n = computed(() => unref(network) || null);
const imperial = computed(() => n.value?.units === 'imperial');

// ── Status ───────────────────────────────────────────────────────
const STATUS = {
    open: { tone: 'open', label: 'Open' },
    caution: { tone: 'caution', label: 'Caution' },
    closed: { tone: 'closed', label: 'Closed' },
};
function bucket(c) {
    if (['open', 'good', 'excellent'].includes(c)) return 'open';
    if (['closed', 'unsafe'].includes(c)) return 'closed';
    return 'caution';
}
const showStatus = computed(() => Boolean(n.value?.show_status));
// "1 of 2 open": amber when some are not, green when all are, red
// when none are.
const openPill = computed(() => {
    const s = n.value?.stats;
    if (!showStatus.value || !s?.trails || s.open == null) return null;
    const tone = s.open === s.trails ? 'open' : s.open === 0 ? 'closed' : 'caution';
    return { tone, label: `${s.open} of ${s.trails} open` };
});
const fmtInt = (v) => Math.round(Number(v)).toLocaleString('en-US');
const metaLine = computed(() => [
    n.value?.location,
    n.value?.stats?.high != null ? `${fmtInt(n.value.stats.high)} ${n.value.stats.height_unit}` : null,
].filter(Boolean).join(' · '));

// Back to wherever the visitor came from on this site, or its Trails page.
const backHref = ref('/trails');
onMounted(() => {
    try {
        const from = document.referrer ? new URL(document.referrer) : null;
        if (from && from.origin === window.location.origin && from.pathname !== window.location.pathname) {
            backHref.value = from.pathname + from.search + from.hash;
        }
    } catch { /* keep the Trails page */ }
});

// ── Hazards ──────────────────────────────────────────────────────
// The trail page's tones, so a hazard reads the same on both pages.
const DESIGN_HAZARD_TONES = {
    danger: { bg: '#d2452f', fg: '#ffffff', soft: '#f8e2df', ink: '#9b2a1f' },
    warning: { bg: '#d2452f', fg: '#ffffff', soft: '#f8e2df', ink: '#9b2a1f' },
    caution: { bg: '#d99a1e', fg: '#ffffff', soft: '#fbf0d9', ink: '#7d5200' },
    notice: { bg: '#6b7f8f', fg: '#ffffff', soft: '#e8ecef', ink: '#3b4a57' },
};
const hazardTone = (level) => DESIGN_HAZARD_TONES[level] || publicHazardTone(level);
const hazards = computed(() => (n.value?.hazards || []).map((h) => ({ ...h, tone: hazardTone(h.level) })));
const reportedDay = (ymd) => parseCalendarDate(ymd)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '';

// ── The map: every trail, the trailheads and the hazards ─────────
const W = 1000, H = 500, PAD = 56;
// Trailhead, Parking, or both — `kinds` lists both when the point is
// both, its own kind first. Older payloads carry `kind` alone.
// Where a trail's page is: the website's own /trails/{slug}, unless the
// page around this one (the /n page) knows better.
const trailHref = inject('tiqTrailHref', (t) => (t?.slug ? `/trails/${t.slug}` : null));
// The trails that start at a trailhead, as the card lists them.
const thTrails = (th) => (Array.isArray(th?.trails) ? th.trails : []).map((t) => ({
    ...t,
    href: trailHref(t),
}));
// One box, a row per location, one open at a time: the first opens on
// its own, opening another closes it, and tapping the open one closes it.
const thOpen = ref(0);
const thIsOpen = (i) => thOpen.value === i;
function thToggle(i) {
    thOpen.value = thOpen.value === i ? -1 : i;
}
const thKinds = (th) => (Array.isArray(th?.kinds) && th.kinds.length ? th.kinds : [th?.kind || 'Trailhead']);
const kindsLabel = (kinds) => (kinds.length > 1 ? `${kinds[0]} and ${kinds.slice(1).join(' and ').toLowerCase()}` : kinds[0]);

const drawing = computed(() => {
    const trails = (n.value?.trails || []).filter((t) => (t.lines || []).some((l) => l.length >= 2));
    if (!trails.length) return null;
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    const grow = (lng, lat) => {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
    };
    for (const t of trails) for (const line of t.lines) for (const [lng, lat] of line) grow(lng, lat);
    for (const th of n.value?.trailheads || []) grow(Number(th.lng), Number(th.lat));
    const cosLat = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180) || 1;
    const dLng = (maxLng - minLng) * cosLat || 1e-6;
    const dLat = (maxLat - minLat) || 1e-6;
    const s = Math.min((W - PAD * 2) / dLng, (H - PAD * 2) / dLat);
    const offX = PAD + ((W - PAD * 2) - dLng * s) / 2;
    const offY = PAD + ((H - PAD * 2) - dLat * s) / 2;
    const project = (lng, lat) => ({ x: offX + (lng - minLng) * cosLat * s, y: offY + (dLat - (lat - minLat)) * s });

    const lines = trails.map((t) => {
        const tone = showStatus.value && t.condition ? bucket(t.condition) : null;
        const d = t.lines.map((line) => line.map(([lng, lat], i) => {
            const p = project(lng, lat);
            return `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
        }).join(' ')).join(' ');
        return { slug: t.slug, name: t.name, d, closed: tone === 'closed', color: t.difficulty_color || t.color || '#1b1d1a' };
    });

    const grid = [];
    for (let i = 1; i < 8; i++) grid.push(`M${(W / 8) * i} 0V${H}`);
    for (let i = 1; i < 4; i++) grid.push(`M0 ${(H / 4) * i}H${W}`);

    const clampPt = (p) => ({ x: Math.min(W - 20, Math.max(20, p.x)), y: Math.min(H - 14, Math.max(24, p.y)) });
    // A point that is both a trailhead and a parking lot draws both
    // signs side by side, its own kind first.
    const heads = (n.value?.trailheads || []).map((th, i) => {
        const kinds = thKinds(th);
        return {
            key: i,
            name: th.name,
            parking: kinds.includes('Parking'),
            label: kindsLabel(kinds),
            th,
            icons: kinds.map((k, j) => ({ parking: k === 'Parking', dx: (j - (kinds.length - 1) / 2) * 36 })),
            ...clampPt(project(Number(th.lng), Number(th.lat))),
        };
    });
    const marks = [];
    for (const h of hazards.value) {
        if (h.lat == null || h.lng == null) continue;
        marks.push({ id: h.key || h.id, fill: h.tone.bg, label: `${h.level_label}: ${h.title}`, hazard: h, ...clampPt(project(Number(h.lng), Number(h.lat))) });
    }
    // Signs that land on top of each other (a trailhead and the lot
    // across the road, a hazard at a trailhead) step apart just far
    // enough to read both, with a short tie back to the true spot.
    const placed = declutter([
        ...heads.map((h) => ({ x: h.x, y: h.y, w: h.icons.length * 36 - 2, h: 36 })),
        ...marks.map((z) => ({ x: z.x, y: z.y - 5, w: 38, h: 34 })),
    ], { bounds: { x0: 4, y0: 4, x1: W - 4, y1: H - 4 } });
    const ties = [];
    heads.forEach((h, i) => {
        const p = placed[i];
        if (p.moved) ties.push({ x1: p.x0, y1: p.y0, x2: p.x, y2: p.y });
        h.x = p.x; h.y = p.y;
    });
    marks.forEach((z, i) => {
        const p = placed[heads.length + i];
        if (p.moved) ties.push({ x1: p.x0, y1: p.y0 + 5, x2: p.x, y2: p.y + 5 });
        z.x = p.x; z.y = p.y + 5;
    });

    // Real contour lines from the DEM around the network (TrailContours
    // on the server, in this same frame); the grid only draws without.
    const contours = contourDrawing(n.value?.contours, project, {
        labelMinLen: 160, labelGap: 140, maxLabels: 7,
        bounds: { x0: 40, y0: 40, x1: W - 100, y1: H - 70 },
    });
    return { lines, grid, contours, heads, marks, ties };
});

// A trail's name shows only while its line is hovered: printed on
// the map, thirty names in one valley overprint each other.
const mapEl = ref(null);
const lineTip = ref(null);
function onLineMove(e, slug) {
    if (!mapEl.value) return;
    const box = mapEl.value.getBoundingClientRect();
    const x = e.clientX - box.left, y = e.clientY - box.top;
    lineTip.value = { slug, x, y, below: y < box.height * 0.38, align: x < box.width * 0.28 ? 'start' : x > box.width * 0.72 ? 'end' : 'mid' };
}
function onLineLeave(slug) {
    if (lineTip.value?.slug === slug) lineTip.value = null;
}
const lineCard = computed(() => {
    const t = lineTip.value;
    const r = t ? trailRows.value.find((x) => (x.slug || x.name) === t.slug) : null;
    if (!r) return null;
    return { ...r, line: [r.meta, r.dist].filter(Boolean).join(' · '), align: t.align, below: t.below, style: { left: `${t.x}px`, top: `${t.y}px` } };
});

// A trailhead, parking or hazard marker gets a card too: hover on a
// desk, tap on a phone (a second tap or a tap elsewhere closes it).
// Anchored at the MARKER rather than the pointer, so it does not
// chase the cursor across a 30 px sign.
const pointTip = ref(null);
function openPointTip(e, key, payload) {
    if (!mapEl.value) return;
    const box = mapEl.value.getBoundingClientRect();
    const r = e.currentTarget.getBoundingClientRect();
    const x = r.left + r.width / 2 - box.left, y = r.top - box.top;
    const below = y < box.height * 0.38;
    pointTip.value = {
        key, ...payload,
        align: x < box.width * 0.28 ? 'start' : x > box.width * 0.72 ? 'end' : 'mid',
        below,
        style: { left: `${x}px`, top: `${below ? r.bottom - box.top : y}px` },
    };
}
function closePointTip(key) {
    if (pointTip.value?.key === key) pointTip.value = null;
}
function togglePointTip(e, key, payload) {
    if (pointTip.value?.key === key && pointTip.value.pinned) { pointTip.value = null; return; }
    openPointTip(e, key, payload);
    pointTip.value.pinned = true;
}
const headTip = (h) => ({
    kind: 'head',
    kinds: thKinds(h.th),
    label: h.label,
    name: h.name,
    meta: [h.th.address, h.th.parking].filter(Boolean).join(' · '),
    amenities: (h.th.amenities || []).slice(0, 4),
});
const hazardTip = (z) => ({
    kind: 'hazard',
    hazard: z.hazard,
    meta: [z.hazard.subject, z.hazard.detail, z.hazard.passable_label].filter(Boolean).join(' · '),
});
function onMapPointerDown(e) {
    if (pointTip.value?.pinned && !e.target.closest?.('.ndp-mark')) pointTip.value = null;
}

// ── Figures under the map ────────────────────────────────────────
const specs = computed(() => {
    const s = n.value?.stats;
    if (!s) return [];
    const out = [{ k: 'Trails', v: String(s.trails), u: '' }];
    if (s.distance > 0) out.push({ k: 'Total distance', v: Number(s.distance).toFixed(1), u: s.distance_unit });
    if (s.open != null) out.push({ k: 'Open today', v: `${s.open}/${s.trails}`, u: '' });
    if (s.low != null) out.push({ k: 'Low point', v: fmtInt(s.low), u: s.height_unit });
    if (s.high != null) out.push({ k: 'High point', v: fmtInt(s.high), u: s.height_unit });
    if (s.climb) out.push({ k: 'Total climb', v: fmtInt(s.climb), u: s.height_unit });
    return out;
});

// ── Details and the difficulty mix ───────────────────────────────
const mix = computed(() => {
    const rows = (n.value?.difficulty_mix || []).filter((m) => m.count > 0);
    const total = rows.reduce((a, m) => a + m.count, 0);
    return total ? rows.map((m) => ({ ...m, label: difficultyLabel(m.difficulty) || m.difficulty, pct: (m.count / total) * 100 })) : [];
});

// ── Trails ───────────────────────────────────────────────────────
const LIMIT = 8;
const showAll = ref(false);
const trailRows = computed(() => (n.value?.trails || []).map((t) => {
    const tone = showStatus.value && t.condition ? bucket(t.condition) : null;
    return {
        ...t,
        href: t.slug ? `/trails/${t.slug}` : null,
        status: tone ? STATUS[tone] : null,
        icon: DIFFICULTY_KEYS.includes(t.difficulty) ? { difficulty: t.difficulty, set: iconSetForUse(t.primary_use), color: t.difficulty_color || t.color } : null,
        meta: [difficultyLabel(t.difficulty), (t.uses || []).join(', ')].filter(Boolean).join(' · '),
        dist: t.distance ? lengthText(t.distance.value, t.distance.unit) : '',
    };
}));
const visibleTrails = computed(() => (showAll.value ? trailRows.value : trailRows.value.slice(0, LIMIT)));

// ── Weather: the site's forecast for this network ─────────────────
const forecast = computed(() => (n.value ? forecastFor(props.site_data, n.value.id) : {}));
const days = computed(() => {
    const slots = forecastSlots(forecast.value, 'daily', 5).filter((d) => d.high_c != null);
    if (!slots.length) return [];
    const lo = Math.min(...slots.map((d) => d.low_c ?? d.high_c));
    const hi = Math.max(...slots.map((d) => d.high_c));
    const span = hi - lo || 1;
    const unit = forecast.value.unit_system || n.value?.units;
    return slots.map((d) => ({
        ...d,
        hi: forecastTemp(d.high_c, unit),
        lo: d.low_c != null ? forecastTemp(d.low_c, unit) : '',
        words: forecastWords(d.code),
        // Pixels in a 60px scale: the bar runs from the day's high to
        // its low, so a cold day sits lower than a warm one.
        top: ((hi - d.high_c) / span) * 60,
        height: Math.max(10, ((d.high_c - (d.low_c ?? d.high_c)) / span) * 60),
        wet: [51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82, 95, 96, 99].includes(d.code),
    }));
});
const weatherHead = computed(() => [metaLine.value, `${days.value.length}-day forecast`].filter(Boolean).join(' · '));

// ── Others ───────────────────────────────────────────────────────
const others = computed(() => (n.value?.others || []).map((o) => ({
    ...o,
    meta: [
        `${o.trails} ${o.trails === 1 ? 'trail' : 'trails'}`,
        o.distance > 0 ? `${Number(o.distance).toFixed(1)} ${o.distance_unit}` : null,
        showStatus.value && o.open != null ? `${o.open} open` : null,
    ].filter(Boolean).join(' · '),
})));

// Whether this part has anything to draw: a block with nothing in it
// takes no room on the page.
const hasPart = computed(() => {
    if (!n.value) return false;
    switch (props.part) {
        case 'header': return true;
        case 'map': return !!(drawing.value || specs.value.length);
        // `mix_only` is /n's: the Difficulty mix bar without the
        // visitor details beside it.
        case 'details': return !!((!props.config?.mix_only && (n.value.details || []).length) || mix.value.length);
        case 'about': return !!n.value.description;
        case 'trails': return true;
        case 'weather': return days.value.length > 0;
        case 'trailheads': return (n.value.trailheads || []).length > 0;
        // No box at all when nothing is reported.
        case 'hazards': return hazards.value.length > 0;
        case 'others': return others.value.length > 0;
        default: return false;
    }
});
</script>

<template>
    <section v-if="hasPart" class="ndp" :class="`ndp--${part}`" :id="config.anchor_id || null">
        <div v-if="part === 'header'" class="ndp-wrap ndp-head">
            <a class="ndp-back ndp-mono" :href="backHref">← All trails</a>
            <div class="ndp-titlerow">
                <h1 class="ndp-h1">{{ n.name }}</h1>
                <span v-if="openPill" class="ndp-pill ndp-mono" :class="`ndp-pill--${openPill.tone}`"><span class="ndp-dot"></span>{{ openPill.label }}</span>
            </div>
            <div v-if="metaLine" class="ndp-mono ndp-meta">{{ metaLine }}</div>
        </div>

        <div v-else-if="part === 'map'" class="ndp-wrap">
            <div v-if="drawing" class="ndp-mapcard" :class="{ 'ndp-mapcard--joined': specs.length }">
                <div ref="mapEl" class="ndp-map" @pointerdown="onMapPointerDown">
                    <svg :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="xMidYMid meet" class="ndp-map__svg" role="img" :aria-label="`Map of ${n.name}`">
                        <g v-if="drawing.contours" aria-hidden="true">
                            <path :d="drawing.contours.minor" fill="none" stroke="#cfc8b0" stroke-width="1.2" stroke-linejoin="round" />
                            <path :d="drawing.contours.index" fill="none" stroke="#b7ab8a" stroke-width="2.2" stroke-linejoin="round" />
                            <text v-for="(l, i) in drawing.contours.labels" :key="`cl${i}`"
                                  :transform="`translate(${l.x.toFixed(1)} ${l.y.toFixed(1)}) rotate(${l.angle.toFixed(1)})`"
                                  text-anchor="middle" dominant-baseline="central" class="ndp-contourtext">{{ l.text }}</text>
                        </g>
                        <template v-else>
                            <path v-for="(g, i) in drawing.grid" :key="`g${i}`" :d="g" fill="none" stroke="#d5d9c8" stroke-width="1.5" />
                        </template>
                        <path v-for="l in drawing.lines" :key="`h-${l.slug || l.name}`" :d="l.d" fill="none" stroke="#fff" stroke-width="10" stroke-linecap="round" stroke-linejoin="round" />
                        <path v-for="l in drawing.lines" :key="`l-${l.slug || l.name}`" :d="l.d" fill="none" stroke-linecap="round" stroke-linejoin="round"
                              :stroke="l.color" :opacity="lineTip && lineTip.slug !== (l.slug || l.name) ? 0.35 : 1"
                              :stroke-width="lineTip?.slug === (l.slug || l.name) ? 6 : 4"
                              :stroke-dasharray="l.closed ? '10 8' : null" class="ndp-line" />
                        <a v-for="l in drawing.lines" :key="`a-${l.slug || l.name}`" :href="l.slug ? `/trails/${l.slug}` : null" :aria-label="l.name">
                            <path :d="l.d" fill="none" stroke="transparent" stroke-width="22" stroke-linecap="round" stroke-linejoin="round" class="ndp-linehit"
                                  @pointermove="onLineMove($event, l.slug || l.name)" @pointerleave="onLineLeave(l.slug || l.name)" />
                        </a>
                        <!-- Parking is the road sign: a blue square with a P. A
                             trailhead is where the trail starts: a dark
                             round marker with a trail flag. -->
                        <g v-for="(t, i) in drawing.ties" :key="`tie${i}`" class="tiq-map-tie" data-marker-tie>
                            <path :d="`M${t.x1} ${t.y1}L${t.x2} ${t.y2}`" stroke="#1b1d1a" stroke-width="2" stroke-opacity=".55" />
                            <circle :cx="t.x1" :cy="t.y1" r="4" fill="#1b1d1a" stroke="#fff" stroke-width="1.5" />
                        </g>
                        <g v-for="h in drawing.heads" :key="`th${h.key}`" :transform="`translate(${h.x} ${h.y})`" :data-th-icons="h.icons.length"
                           class="ndp-mark" tabindex="0" role="button" :aria-label="`${h.label}: ${h.name}`"
                           @pointerenter="(e) => e.pointerType === 'mouse' && openPointTip(e, `th${h.key}`, headTip(h))"
                           @pointerleave="(e) => e.pointerType === 'mouse' && !pointTip?.pinned && closePointTip(`th${h.key}`)"
                           @focus="openPointTip($event, `th${h.key}`, headTip(h))" @blur="closePointTip(`th${h.key}`)"
                           @click="togglePointTip($event, `th${h.key}`, headTip(h))">
                            <g v-for="(ic, j) in h.icons" :key="j" :transform="`translate(${ic.dx} 0)`">
                                <template v-if="ic.parking">
                                    <rect x="-15" y="-15" width="30" height="30" rx="5" fill="#1f5fbf" stroke="#fff" stroke-width="3" />
                                    <text y="1" text-anchor="middle" dominant-baseline="central" class="ndp-ptext">P</text>
                                </template>
                                <template v-else>
                                    <circle r="16" fill="#1d4d34" stroke="#fff" stroke-width="3" />
                                    <path d="M-5 9 V-9 M-5 -9 H7 L4 -4.5 L7 0 H-5" fill="none" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />
                                </template>
                            </g>
                        </g>
                        <g v-for="z in drawing.marks" :key="`hz${z.id}`" :transform="`translate(${z.x} ${z.y})`"
                           class="ndp-mark" tabindex="0" role="button" :aria-label="z.label" data-hazard-mark
                           @pointerenter="(e) => e.pointerType === 'mouse' && openPointTip(e, `hz${z.id}`, hazardTip(z))"
                           @pointerleave="(e) => e.pointerType === 'mouse' && !pointTip?.pinned && closePointTip(`hz${z.id}`)"
                           @focus="openPointTip($event, `hz${z.id}`, hazardTip(z))" @blur="closePointTip(`hz${z.id}`)"
                           @click="togglePointTip($event, `hz${z.id}`, hazardTip(z))">
                            <path d="M0 -20 L17 10 L-17 10 Z" :fill="z.fill" stroke="#fff" stroke-width="3" stroke-linejoin="round" />
                            <path d="M0 -9 V0 M0 4.5 V5" stroke="#fff" stroke-width="3" stroke-linecap="round" />
                        </g>
                    </svg>
                    <div v-if="pointTip" class="ndp-tip ndp-tip--point" :class="[`ndp-tip--${pointTip.align}`, { 'ndp-tip--below': pointTip.below }]" :style="pointTip.style" role="tooltip" data-point-tip>
                        <template v-if="pointTip.kind === 'head'">
                            <div class="ndp-mono ndp-k ndp-th__kind"><span v-for="k in pointTip.kinds" :key="k" :class="k === 'Parking' ? 'ndp-legend__p' : 'ndp-legend__th'" aria-hidden="true">{{ k === 'Parking' ? 'P' : '' }}</span>{{ pointTip.label }}</div>
                            <div class="ndp-tip__title">{{ pointTip.name }}</div>
                            <div v-if="pointTip.meta" class="ndp-tip__meta">{{ pointTip.meta }}</div>
                            <div v-if="pointTip.amenities.length" class="ndp-th__tags">
                                <span v-for="a in pointTip.amenities" :key="a" class="ndp-th__tag">{{ a }}</span>
                            </div>
                        </template>
                        <template v-else>
                            <span class="ndp-mono ndp-tip__sev" :style="{ background: pointTip.hazard.tone.bg, color: pointTip.hazard.tone.fg }">{{ pointTip.hazard.level_label }}</span>
                            <div class="ndp-tip__title"><HazardIcon :hazard="pointTip.hazard" :size="14" class="ndp-haz__type" />{{ pointTip.hazard.title }}</div>
                            <div v-if="pointTip.hazard.trail_name" class="ndp-mono ndp-k">{{ pointTip.hazard.trail_name }}</div>
                            <div v-if="pointTip.meta" class="ndp-tip__meta">{{ pointTip.meta }}</div>
                        </template>
                    </div>
                    <div v-if="lineCard && !pointTip" class="ndp-tip" :class="[`ndp-tip--${lineCard.align}`, { 'ndp-tip--below': lineCard.below }]" :style="lineCard.style" role="tooltip">
                        <div class="ndp-tip__title">
                            <DifficultyIcon v-if="lineCard.icon" :difficulty="lineCard.icon.difficulty" :icon-set="lineCard.icon.set" :color="lineCard.icon.color" class="ndp-row__icon" aria-hidden="true" />
                            {{ lineCard.name }}
                        </div>
                        <div v-if="lineCard.line" class="ndp-tip__meta">{{ lineCard.line }}</div>
                        <span v-if="lineCard.status" class="ndp-pill ndp-pill--sm ndp-mono" :class="`ndp-pill--${lineCard.status.tone}`"><span class="ndp-dot"></span>{{ lineCard.status.label }}</span>
                    </div>
                    <div class="ndp-legend ndp-mono">
                        <span v-if="drawing.heads.some((h) => h.icons.some((ic) => !ic.parking))"><span class="ndp-legend__th"></span>Trailhead</span>
                        <span v-if="drawing.heads.some((h) => h.parking)"><span class="ndp-legend__p">P</span>Parking</span>
                        <span v-for="m in mix" :key="`lg-${m.difficulty}`"><span class="ndp-legend__line" :style="{ background: m.color }"></span>{{ m.label }}</span>
                        <span v-if="!mix.length"><span class="ndp-legend__line"></span>Trail</span>
                        <span v-if="drawing.lines.some((l) => l.closed)"><span class="ndp-legend__dash"></span>Closed</span>
                        <span v-if="drawing.marks.length"><span class="ndp-legend__haz"></span>Hazard</span>
                    </div>
                </div>
            </div>
            <div v-if="specs.length" class="ndp-specs" :class="{ 'ndp-specs--alone': !drawing }" :style="{ '--ndp-cols': Math.min(6, specs.length) }">
                <div v-for="sp in specs" :key="sp.k" class="ndp-spec">
                    <div class="ndp-mono ndp-k">{{ sp.k }}</div>
                    <div class="ndp-spec__row"><span class="ndp-spec__v">{{ sp.v }}</span><span v-if="sp.u" class="ndp-spec__u">{{ sp.u }}</span></div>
                </div>
            </div>
        </div>

        <div v-else-if="part === 'details'" class="ndp-wrap">
            <div class="ndp-details">
                <div v-if="!config.mix_only && (n.details || []).length" class="ndp-details__grid">
                    <div v-for="d in n.details" :key="d.key" class="ndp-detail">
                        <div class="ndp-mono ndp-k">{{ d.label }}</div>
                        <div class="ndp-detail__v">{{ d.value }}</div>
                    </div>
                </div>
                <div v-if="mix.length" class="ndp-mix">
                    <div class="ndp-mono ndp-k">{{ heading('Difficulty mix', 'mix_title') }}</div>
                    <div class="ndp-mix__bar">
                        <span v-for="m in mix" :key="m.difficulty" :style="{ width: `${m.pct}%`, background: m.color }" :title="`${m.label}: ${m.count}`"></span>
                    </div>
                    <div class="ndp-mix__legend">
                        <span v-for="m in mix" :key="m.difficulty"><span class="ndp-mix__sw" :style="{ background: m.color }"></span>{{ m.label }} · {{ m.count }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="part === 'about'" class="ndp-wrap ndp-part">
            <h2 class="ndp-h2">{{ heading('About this network') }}</h2>
            <p class="ndp-lede">{{ n.description }}</p>
        </div>

        <div v-else-if="part === 'trails'" class="ndp-wrap ndp-part">
            <h2 class="ndp-h2">{{ heading('Trails in this network') }}</h2>
            <div v-if="trailRows.length" class="ndp-rows">
                <component :is="t.href ? 'a' : 'div'" v-for="t in visibleTrails" :key="t.slug || t.name" :href="t.href" class="ndp-row">
                    <span class="ndp-row__text">
                        <span class="ndp-row__name"><DifficultyIcon v-if="t.icon" :difficulty="t.icon.difficulty" :icon-set="t.icon.set" :color="t.icon.color" class="ndp-row__icon" aria-hidden="true" />{{ t.name }}</span>
                        <span v-if="t.meta" class="ndp-row__meta">{{ t.meta }}</span>
                    </span>
                    <span v-if="t.dist" class="ndp-mono ndp-row__dist">{{ t.dist }}</span>
                    <span v-if="t.status" class="ndp-pill ndp-pill--sm ndp-mono" :class="`ndp-pill--${t.status.tone}`"><span class="ndp-dot"></span>{{ t.status.label }}</span>
                    <span v-if="t.href" class="ndp-row__go" aria-hidden="true">→</span>
                </component>
            </div>
            <p v-else class="ndp-empty">No trails in this network yet.</p>
            <button v-if="trailRows.length > LIMIT" type="button" class="ndp-more ndp-more--btn" @click="showAll = !showAll">
                {{ showAll ? 'Show fewer' : `Show all ${trailRows.length} trails` }}
            </button>
        </div>

        <div v-else-if="part === 'weather'" class="ndp-wrap ndp-part">
            <h2 class="ndp-h2">{{ heading('Weather') }}</h2>
            <div class="ndp-wx">
                <div class="ndp-mono ndp-wx__head">{{ weatherHead }}</div>
                <div class="ndp-wx__days">
                    <div v-for="d in days" :key="d.when" class="ndp-wx__day">
                        <div class="ndp-mono ndp-wx__when">{{ d.when }}</div>
                        <div class="ndp-wx__plot">
                            <div class="ndp-wx__hi" :style="{ top: `${d.top}px` }">{{ d.hi }}</div>
                            <div class="ndp-wx__bar" :class="{ 'is-wet': d.wet }" :style="{ top: `${d.top + 22}px`, height: `${d.height}px` }"></div>
                            <div class="ndp-wx__lo" :style="{ top: `${d.top + d.height + 28}px` }">{{ d.lo }}</div>
                        </div>
                        <div class="ndp-wx__words">{{ d.words }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="part === 'trailheads'" class="ndp-wrap ndp-part">
            <section class="ndp-ths" data-ndp-trailheads>
                <header class="ndp-ths__head">
                    <h2 class="ndp-ths__title">{{ heading('Trailheads & parking') }}</h2>
                    <div class="ndp-mono ndp-k">{{ n.trailheads.length }} {{ n.trailheads.length === 1 ? 'location' : 'locations' }}</div>
                </header>
                <div v-for="(th, i) in n.trailheads" :key="i" class="ndp-th" :class="{ 'is-open': thIsOpen(i) }">
                    <button type="button" class="ndp-th__bar" :aria-expanded="thIsOpen(i) ? 'true' : 'false'" :aria-controls="`ndp-th-${i}`" @click="thToggle(i)">
                        <span class="ndp-th__badge" aria-hidden="true">
                            <!-- The map's own signs: a blue P square, a green round trailhead flag. -->
                            <svg v-for="k in thKinds(th)" :key="k" viewBox="-17 -17 34 34" class="ndp-th__sign">
                                <template v-if="k === 'Parking'">
                                    <rect x="-15" y="-15" width="30" height="30" rx="5" fill="#1f5fbf" />
                                    <text y="1" text-anchor="middle" dominant-baseline="central" class="ndp-ptext">P</text>
                                </template>
                                <template v-else>
                                    <circle r="16" fill="#1d4d34" />
                                    <path d="M-5 9 V-9 M-5 -9 H7 L4 -4.5 L7 0 H-5" fill="none" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />
                                </template>
                            </svg>
                        </span>
                        <span class="ndp-th__id">
                            <span class="ndp-th__name">{{ th.name }}</span>
                            <span class="ndp-mono ndp-k ndp-th__kind">{{ thKinds(th).join(' + ') }}</span>
                        </span>
                        <span class="ndp-th__toggle" aria-hidden="true">{{ thIsOpen(i) ? '−' : '+' }}</span>
                    </button>
                    <div v-show="thIsOpen(i)" :id="`ndp-th-${i}`" class="ndp-th__body">
                        <div v-if="th.address" class="ndp-th__address">{{ th.address }}</div>
                        <div v-if="th.parking" class="ndp-th__line">{{ th.parking }}</div>
                        <p v-if="th.description" class="ndp-th__note">{{ th.description }}</p>
                        <div v-if="thTrails(th).length" class="ndp-th__group" data-ndp-th-trails>
                            <div class="ndp-mono ndp-k">{{ heading('Trails from here', 'trails_from_here_title') }}</div>
                            <div class="ndp-th__tags">
                                <component :is="t.href ? 'a' : 'span'" v-for="t in thTrails(th)" :key="t.slug || t.name" :href="t.href" class="ndp-th__tag ndp-th__tag--trail">{{ t.name }}</component>
                            </div>
                        </div>
                        <div v-if="th.amenities.length" class="ndp-th__group">
                            <div class="ndp-mono ndp-k">Amenities</div>
                            <div class="ndp-th__tags">
                                <span v-for="a in th.amenities" :key="a" class="ndp-th__tag">{{ a }}</span>
                            </div>
                        </div>
                        <a :href="th.directions_url" class="ndp-th__go" target="_blank" rel="noopener">Get directions <span aria-hidden="true">↗</span></a>
                    </div>
                </div>
            </section>
        </div>

        <div v-else-if="part === 'hazards'" class="ndp-wrap ndp-part">
            <div class="ndp-part__head">
                <h2 class="ndp-h3">{{ heading('Hazards') }}</h2>
                <a v-if="n.report_url" :href="n.report_url" class="ndp-more">Report a hazard →</a>
            </div>
            <div v-for="z in hazards" :key="z.key || z.id" class="ndp-haz" :style="{ background: z.tone.soft }">
                <svg width="22" height="22" viewBox="0 0 24 24" class="ndp-haz__icon" aria-hidden="true">
                    <path d="M12 3 L22 20 H2 Z" :fill="z.tone.bg" stroke-linejoin="round" />
                    <path d="M12 9.5 V14 M12 17 V17.2" :stroke="z.tone.fg" stroke-width="2.2" stroke-linecap="round" />
                </svg>
                <div class="ndp-haz__text">
                    <div class="ndp-haz__top">
                        <span class="ndp-haz__title"><HazardIcon :hazard="z" :size="15" class="ndp-haz__type" />{{ z.title }}</span>
                        <span class="ndp-mono ndp-haz__sev" :style="{ color: z.tone.ink }">{{ z.level_label }}<template v-if="z.trail_name"> · <a v-if="z.trail_slug" :href="`/trails/${z.trail_slug}`" class="ndp-haz__trail">{{ z.trail_name }}</a><template v-else>{{ z.trail_name }}</template></template></span>
                    </div>
                    <div v-if="z.subject || z.detail || z.passable_label" class="ndp-haz__detail">{{ [z.subject, z.detail, z.passable_label].filter(Boolean).join(' · ') }}</div>
                    <div v-if="z.reported_on" class="ndp-mono ndp-haz__by">{{ z.reported_verb || 'Reported' }} {{ reportedDay(z.reported_on) }}</div>
                </div>
            </div>
            <div v-if="!hazards.length" class="ndp-nohaz"><span class="ndp-nohaz__dot"></span>No hazards reported in this network.</div>
        </div>

        <div v-else-if="part === 'others'" class="ndp-wrap ndp-part">
            <h2 class="ndp-h3">{{ heading('Other networks') }}</h2>
            <div class="ndp-rows">
                <a v-for="o in others" :key="o.slug" :href="o.href" class="ndp-row ndp-row--tight">
                    <span class="ndp-row__text">
                        <span class="ndp-row__name">{{ o.name }}</span>
                        <span class="ndp-mono ndp-row__mmeta">{{ o.meta }}</span>
                    </span>
                    <span class="ndp-row__go" aria-hidden="true">→</span>
                </a>
            </div>
        </div>
    </section>
</template>

<style scoped>
.ndp {
    --ndp-ink: var(--th-ink, #1b1d1a);
    --ndp-line: var(--th-line, #e3e1da);
    --ndp-rule: var(--th-rule, #d9d6cd);
    --ndp-meta: var(--th-meta, #5c6159);
    --ndp-muted: var(--th-muted, #4d524b);
    --ndp-mono: var(--th-mono, ui-monospace, 'SFMono-Regular', Menlo, monospace);
    --ndp-display: var(--th-display, 'Barlow', system-ui, sans-serif);
    color: var(--ndp-ink);
}
.ndp-wrap { max-width: 1240px; margin: 0 auto; padding-left: clamp(16px, 4vw, 40px); padding-right: clamp(16px, 4vw, 40px); }
.ndp-mono { font: 500 11px/1.5 var(--ndp-mono); letter-spacing: .1em; text-transform: uppercase; }
.ndp-k { color: var(--ndp-meta); font-size: 10px; line-height: 1; }
.ndp--map, .ndp--details { padding-bottom: 12px; }
.ndp-part { display: flex; flex-direction: column; gap: 12px; padding-top: 12px; padding-bottom: 24px; }

.ndp-head { padding-top: clamp(24px, 3vw, 36px); padding-bottom: 20px; }
/* See TrailDetail: a plain ancestor, never `:global(.tpl-classic) .ndp-head`. */
.tpl-classic .ndp-head { padding-top: calc(68px + clamp(24px, 3vw, 36px)); }
.ndp-back { display: inline-block; color: var(--ndp-meta); margin-bottom: 18px; text-decoration: none; }
.ndp-back:hover { color: var(--ndp-ink); text-decoration: none; }
.ndp-titlerow { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 16px; }
.ndp-h1 { font: 800 clamp(34px, 5vw, 60px)/1 var(--ndp-display); font-stretch: 110%; letter-spacing: -.02em; margin: 0; overflow-wrap: anywhere; }
.ndp-meta { color: var(--ndp-meta); margin-top: 14px; }
.ndp-pill { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 999px; line-height: 1; white-space: nowrap; }
.ndp-pill--sm { padding: 4px 9px; font-size: 10px; }
.ndp-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.ndp-pill--open { background: #e3f1e6; color: #1d6b35; }
.ndp-pill--open .ndp-dot { background: #2e9b4f; }
.ndp-pill--caution { background: #fbf0d9; color: #7d5200; }
.ndp-pill--caution .ndp-dot { background: #d99a1e; }
.ndp-pill--closed { background: #f8e2df; color: #9b2a1f; }
.ndp-pill--closed .ndp-dot { background: #d2452f; }

.ndp-mapcard { border: 1px solid var(--ndp-line); border-radius: var(--r, 6px); overflow: hidden; background: #fff; }
.ndp-mapcard--joined { border-radius: var(--r, 6px) var(--r, 6px) 0 0; }
.ndp-map { position: relative; aspect-ratio: 2 / 1; background: #eef0e6; overflow: hidden; }
@media (max-width: 640px) { .ndp-map { aspect-ratio: 4 / 3; } }
.ndp-map__svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; overflow: visible; }
.ndp-line { pointer-events: none; transition: stroke-width .12s, opacity .12s; }
.ndp-linehit { pointer-events: stroke; cursor: pointer; }
.ndp-contourtext { font: 600 14px/1 var(--ndp-mono); fill: #8f7f58; letter-spacing: .04em; stroke: #eef0e6; stroke-width: 5px; paint-order: stroke; }
/* The viewBox is 1000 wide, so on a phone 14 units is under 5 px. */
@media (max-width: 640px) { .ndp-contourtext { font-size: 30px; stroke-width: 9px; } }
.ndp-tip { position: absolute; z-index: 3; pointer-events: none; min-width: 180px; max-width: 280px; padding: 10px 12px; background: #fff;
    border: 1px solid var(--ndp-line); border-radius: var(--r, 6px); box-shadow: 0 6px 18px rgba(0, 0, 0, .12);
    transform: translate(-50%, calc(-100% - 14px)); display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
.ndp-tip--start { transform: translate(-12px, calc(-100% - 14px)); }
.ndp-tip--end { transform: translate(calc(-100% + 12px), calc(-100% - 14px)); }
.ndp-tip--below.ndp-tip--mid { transform: translate(-50%, 18px); }
.ndp-tip--below.ndp-tip--start { transform: translate(-12px, 18px); }
.ndp-tip--below.ndp-tip--end { transform: translate(calc(-100% + 12px), 18px); }
.ndp-tip__title { display: flex; align-items: center; gap: 7px; font-weight: 700; font-size: 15px; line-height: 1.25; color: var(--ndp-ink); }
.ndp-tip__meta { font-size: 13px; color: var(--ndp-meta); }
.ndp-tip__sev { padding: 3px 7px; border-radius: 3px; font-size: 10px; line-height: 1.2; letter-spacing: .06em; text-transform: uppercase; }
.ndp-tip--point { transform: translate(-50%, calc(-100% - 8px)); }
.ndp-tip--point.ndp-tip--start { transform: translate(-22px, calc(-100% - 8px)); }
.ndp-tip--point.ndp-tip--end { transform: translate(calc(-100% + 22px), calc(-100% - 8px)); }
.ndp-tip--point.ndp-tip--below { transform: translate(-50%, 8px); }
.ndp-tip--point.ndp-tip--below.ndp-tip--start { transform: translate(-22px, 8px); }
.ndp-tip--point.ndp-tip--below.ndp-tip--end { transform: translate(calc(-100% + 22px), 8px); }
.ndp-mark { cursor: pointer; outline: none; }
.ndp-mark:focus-visible { filter: drop-shadow(0 0 3px #1b1d1a); }
.ndp-ptext { font: 800 17px/1 var(--ndp-display); fill: #fff; }
.ndp-legend { position: absolute; left: 12px; bottom: 12px; display: flex; flex-wrap: wrap; gap: 6px 14px; background: rgba(255, 255, 255, .92);
    padding: 8px 10px; border-radius: var(--r, 6px); font-size: 10px; line-height: 1; color: #1b1d1a; }
.ndp-legend > span { display: flex; align-items: center; gap: 6px; }
.ndp-legend__p, .ndp-legend__th { display: inline-block; flex: none; }
.ndp-legend__p { width: 12px; height: 12px; border-radius: 2px; background: #1f5fbf; color: #fff; font: 800 9px/12px var(--ndp-display); text-align: center; letter-spacing: 0; }
.ndp-legend__th { width: 12px; height: 12px; border-radius: 50%; background: #1d4d34; }
.ndp-legend__line { width: 14px; height: 3px; background: #1b1d1a; }
.ndp-legend__dash { width: 14px; border-top: 3px dashed #1b1d1a; }
.ndp-legend__haz { width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent; border-bottom: 10px solid #d2452f; }

/* As many columns as figures (up to six), and hairlines drawn by each
   cell rather than by the gap: a network with five figures leaves no
   empty grey cell at the end of a row. */
.ndp-specs { display: grid; grid-template-columns: repeat(var(--ndp-cols, 6), minmax(0, 1fr)); background: #fff; border: 1px solid var(--ndp-line);
    border-top: 0; border-radius: 0 0 var(--r, 6px) var(--r, 6px); overflow: hidden; }
.ndp-specs--alone { border-top: 1px solid var(--ndp-line); border-radius: var(--r, 6px); }
@media (max-width: 1000px) { .ndp-specs { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 560px) { .ndp-specs { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.ndp-spec { background: #fff; box-shadow: 1px 0 0 var(--ndp-line), 0 1px 0 var(--ndp-line); padding: 18px 20px; display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.ndp-spec__row { display: flex; align-items: baseline; gap: 5px; white-space: nowrap; min-width: 0; }
.ndp-spec__v { font: 700 clamp(22px, 2vw, 28px)/1 var(--ndp-mono); letter-spacing: -.02em; color: var(--ndp-ink); overflow: hidden; text-overflow: ellipsis; }
.ndp-spec__u { font: 500 13px/1 var(--ndp-mono); color: var(--ndp-meta); }

.ndp-details { border-top: 1px solid var(--ndp-line); margin-top: 16px; padding-top: 24px; display: flex; flex-direction: column; gap: 24px; }
.ndp-details__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px 32px; }
@media (max-width: 560px) { .ndp-details__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.ndp-detail { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.ndp-detail__v { font-size: 18px; line-height: 1.35; color: var(--ndp-ink); text-wrap: pretty; }
.ndp-mix { display: flex; flex-direction: column; gap: 10px; }
.ndp-mix__bar { display: flex; height: 8px; border-radius: 999px; overflow: hidden; gap: 2px; }
.ndp-mix__bar > span { display: block; min-width: 4px; }
.ndp-mix__legend { display: flex; flex-wrap: wrap; gap: 6px 18px; font-size: 15px; color: var(--ndp-ink); }
.ndp-mix__legend > span { display: inline-flex; align-items: center; gap: 6px; }
.ndp-mix__sw { width: 9px; height: 9px; border-radius: 2px; }

.ndp-h2 { font: 800 clamp(24px, 2.6vw, 30px)/1.1 var(--ndp-display); font-stretch: 106%; margin: 0; }
.ndp-h3 { font: 800 22px/1.15 var(--ndp-display); margin: 0; }
.ndp-lede { margin: 0; font-size: 18px; line-height: 1.6; color: #363a35; max-width: 68ch; white-space: pre-line; }
.ndp-part__head { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: baseline; gap: 8px 16px; }
.ndp-more { font-weight: 600; color: var(--ndp-ink); border-bottom: 2px solid var(--accent); text-decoration: none; align-self: flex-start; }
.ndp-more:hover { text-decoration: none; border-bottom-color: var(--ndp-ink); }
.ndp-more--btn { background: none; padding: 0; cursor: pointer; font-size: 15px; }
.ndp-empty { margin: 0; color: var(--ndp-muted); }

.ndp-rows { display: flex; flex-direction: column; background: #fff; border: 1px solid var(--ndp-line); border-radius: var(--r, 6px); overflow: hidden; }
.ndp-row { display: flex; align-items: center; gap: 14px; padding: 16px 18px; border-bottom: 1px solid #eeece6; color: inherit; text-decoration: none; }
.ndp-row--tight { padding: 13px 16px; }
.ndp-row:last-child { border-bottom: 0; }
a.ndp-row:hover { background: #faf9f6; text-decoration: none; }
.ndp-row__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.ndp-row__name { display: inline-flex; align-items: center; gap: 7px; font-weight: 700; font-size: 17px; line-height: 1.2; color: var(--ndp-ink); }
.ndp-row__icon { width: 13px; height: 13px; flex: none; }
.ndp-row__meta { font-size: 14px; color: var(--ndp-meta); }
.ndp-row__mmeta { font-size: 10px; color: var(--ndp-meta); letter-spacing: .08em; }
.ndp-row__dist { font-size: 13px; color: var(--ndp-ink); flex: none; }
.ndp-row__go { font-size: 16px; color: var(--ndp-meta); flex: none; }
@media (max-width: 520px) { .ndp-row__dist { display: none; } }

.ndp-wx { background: #fff; border: 1px solid var(--ndp-line); border-radius: var(--r, 6px); overflow: hidden; }
.ndp-wx__head { padding: 12px 16px; border-bottom: 1px solid #eeece6; font-size: 10px; color: var(--ndp-meta); }
.ndp-wx__days { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); }
.ndp-wx__day { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 16px 6px; border-left: 1px solid #eeece6; min-width: 0; }
.ndp-wx__day:first-child { border-left: 0; }
.ndp-wx__when { font-weight: 700; color: var(--ndp-ink); font-size: 10px; }
.ndp-wx__plot { position: relative; width: 100%; height: 112px; }
.ndp-wx__hi { position: absolute; left: 0; right: 0; text-align: center; font: 700 15px/1 var(--ndp-mono); color: var(--ndp-ink); }
.ndp-wx__lo { position: absolute; left: 0; right: 0; text-align: center; font: 500 12px/1 var(--ndp-mono); color: var(--ndp-meta); }
.ndp-wx__bar { position: absolute; left: 50%; width: 8px; margin-left: -4px; border-radius: 999px; background: var(--accent, #2e6b46); }
.ndp-wx__bar.is-wet { background: #6f8fa8; }
.ndp-wx__words { font-size: 13px; color: var(--ndp-muted); text-align: center; }

.ndp-ths { background: var(--tint, #e4ebe5); border-radius: var(--r, 6px); padding: 22px 18px 18px; display: flex; flex-direction: column; gap: 12px; }
.ndp-ths__head { display: flex; flex-direction: column; gap: 8px; padding: 0 4px 6px; }
.ndp-ths__title { margin: 0; font: 800 26px/1.15 var(--ndp-display); color: var(--ndp-ink); }
.ndp-th { background: #fff; border: 1px solid var(--ndp-line, #dcdad3); border-radius: var(--r, 6px); overflow: hidden; }
.ndp-th.is-open { border-color: var(--ndp-ink); }
.ndp-th__bar { width: 100%; display: flex; align-items: center; gap: 14px; padding: 16px; background: none; border: 0; text-align: left; cursor: pointer; color: inherit; font: inherit; }
.ndp-th.is-open .ndp-th__bar { border-bottom: 1px solid #eeece6; }
.ndp-th__badge { flex: none; display: inline-flex; gap: 4px; align-items: center; }
.ndp-th__sign { width: 30px; height: 30px; display: block; }
.ndp-th__id { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.ndp-th__name { font: 700 19px/1.2 var(--ndp-display); color: var(--ndp-ink); }
.ndp-th__kind { display: flex; align-items: center; gap: 7px; }
.ndp-th__id .ndp-th__kind { display: block; }
.ndp-th__toggle { flex: none; font-size: 22px; line-height: 1; color: var(--ndp-meta); width: 20px; text-align: center; }
.ndp-th__body { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.ndp-th__address { font-size: 18px; color: var(--ndp-ink); }
.ndp-th__line { font-size: 16px; color: var(--ndp-muted); }
.ndp-th__group { display: flex; flex-direction: column; gap: 8px; }
.ndp-th__tags { display: flex; flex-wrap: wrap; gap: 6px; }
.ndp-th__tag { background: #f3f2ee; border: 1px solid var(--ndp-rule, #e2e0da); border-radius: 999px; padding: 7px 12px; font-size: 14px; line-height: 1; color: var(--ndp-ink); }
.ndp-th__tag--trail { background: var(--ndp-ink, #1b1d1a); border-color: var(--ndp-ink, #1b1d1a); color: #fff; text-decoration: none; }
a.ndp-th__tag--trail:hover { background: #000; text-decoration: none; }
.ndp-th__note { margin: 0; font-size: 15px; color: var(--ndp-muted); white-space: pre-line; }
.ndp-th__go { margin-top: 4px; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 16px; border-radius: var(--r, 6px); background: var(--accent, #3f6b4e); color: #fff; font-weight: 600; text-decoration: none; }
.ndp-th__go:hover { filter: brightness(.94); text-decoration: none; }

.ndp-haz { border-radius: var(--r, 6px); padding: 16px 18px; display: flex; gap: 14px; align-items: flex-start; }
.ndp-haz__icon { flex: none; margin-top: 1px; }
.ndp-haz__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.ndp-haz__top { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 10px; }
.ndp-haz__title { font: 700 17px/1.2 var(--ndp-display); }
.ndp-haz__type { margin-right: 6px; }
.ndp-haz__sev { font-size: 10px; }
.ndp-haz__trail { color: inherit; text-decoration: none; }
.ndp-haz__trail:hover { text-decoration: underline; }
.ndp-haz__detail { font-size: 15px; color: #363a35; }
.ndp-haz__by { font-size: 10px; color: var(--ndp-meta); }
.ndp-nohaz { border: 1px dashed #cfccc2; border-radius: var(--r, 6px); padding: 16px 18px; display: flex; align-items: center; gap: 12px; font-size: 16px; color: var(--ndp-muted); }
.ndp-nohaz__dot { width: 9px; height: 9px; border-radius: 50%; background: #2e9b4f; flex: none; }
</style>

<style>
/* Unscoped, like the trail page's: a Columns block holding network
   parts lines up with the parts above it. */
.tiq-cols .ndp-wrap, .th-cols .ndp-wrap { max-width: none; padding-left: 0; padding-right: 0; }
.tiq-cols:has(.ndp) { padding-left: 0; padding-right: 0; padding-top: 8px; }
.tiq-cols:has(.ndp) .tiq-cols-grid {
    max-width: 1240px; box-sizing: border-box;
    padding-left: clamp(16px, 4vw, 40px); padding-right: clamp(16px, 4vw, 40px);
}
.th-cols:has(.ndp) { padding-top: 8px; }
.tiq-cols-col > .ndp { padding: 0 !important; border-radius: 0; }
</style>
